import { useEffect, useState } from 'react';
import { StanButton, StanLoader, StanModal } from '@/components';
import { useInstagramPosts } from '../../queries/useAutomationQueries';
import { useInstagramIntegration } from '../../hooks/useInstagramIntegration';
import { cn, createIconElement } from '@/lib';
import type { InstagramPost } from '../../types/automation.types';
import IconEmptyTable from '@/assets/icons/ic-empty-table.svg';
import IcCheck from '@/assets/icons/ic-check.svg';

interface PostPickerModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedPostIds: string[];
    onConfirm: (postIds: string[]) => void;
}

/**
 * PostPickerModal - Modal for selecting Instagram posts
 * Allows users to select up to 5 posts to trigger automation on
 */
export const PostPickerModal = ({ isOpen, onClose, selectedPostIds, onConfirm }: PostPickerModalProps) => {
    const { instagramStatus } = useInstagramIntegration();
    const isInstagramConnected = instagramStatus === 'connected';
    const { data: postsResponse, isLoading } = useInstagramPosts(isInstagramConnected);
    const posts = postsResponse?.data || [];
    const [tempSelectedIds, setTempSelectedIds] = useState<string[]>(selectedPostIds);

    useEffect(() => {
        if (isOpen) {
            setTempSelectedIds(selectedPostIds);
        }
    }, [isOpen, selectedPostIds]);

    const togglePost = (postId: string) => {
        const isSelected = tempSelectedIds.includes(postId);
        if (isSelected) {
            setTempSelectedIds(tempSelectedIds.filter(id => id !== postId));
        } else {
            setTempSelectedIds([...tempSelectedIds, postId]);
        }
    };

    const handleConfirm = () => {
        onConfirm(tempSelectedIds);
        onClose();
    };

    const handleClearAll = () => {
        setTempSelectedIds([]);
    };

    const getButtonText = () => {
        if (tempSelectedIds.length === 0) {
            return 'Enable for All Posts';
        }
        return `Enable for ${tempSelectedIds.length} Post${tempSelectedIds.length === 1 ? '' : 's'}`;
    };

    return (
        <StanModal
            id='post-picker-modal'
            isOpen={isOpen}
            onClose={onClose}
            title={posts.length === 9 ? 'Select Posts' : ''}
            size='sm'
        >
            <div className='flex flex-col gap-4'>
                {isLoading ? (
                    <div className='flex items-center justify-center py-12'>
                        <StanLoader />
                    </div>
                ) : posts.length === 0 ? (
                    <div className='mt-4 flex flex-col items-center gap-6 text-center md:gap-8'>
                        <IconEmptyTable className='size-20' />

                        <div className='space-y-2'>
                            <p className='md:text-body-lg font-semibold'>No Posts Found</p>
                            <p className='text-caption-1-regular text-gray mt-1'>
                                Make sure your Instagram account is connected and has posts.
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className='w-full'>
                        <div className='grid max-h-96 grid-cols-3 gap-2.5 overflow-x-visible overflow-y-auto p-0.5 pt-1'>
                            {posts.map((post: InstagramPost) => {
                                const isSelected = tempSelectedIds.includes(post.id);

                                return (
                                    <button
                                        key={post.id}
                                        type='button'
                                        onClick={() => togglePost(post.id)}
                                        className={cn(
                                            'border-neutral-inverse group hover:shadow-nucleus-elevation-high focus-visible:shadow-nucleus-elevation-high relative aspect-square overflow-hidden rounded-md border-2 shadow-md transition duration-300 hover:-translate-y-0.5 focus-visible:-translate-y-0.5',
                                            { 'border-brand-main ring-brand-main ring-2': isSelected }
                                        )}
                                    >
                                        <img
                                            src={post.thumbnail_url || post.media_url}
                                            alt={post.caption || 'Instagram post'}
                                            className='h-full w-full object-cover'
                                        />

                                        {/* Selected overlay */}
                                        {isSelected && (
                                            <div className='bg-canvas/50 absolute inset-0 transition-opacity'>
                                                <div className='bg-brand-main border-canvas absolute right-2 bottom-2 flex size-6 items-center justify-center rounded-full border-2'>
                                                    {createIconElement(IcCheck, { className: 'size-4 text-canvas' })}
                                                </div>
                                            </div>
                                        )}

                                        {/* Hover overlay with caption */}
                                        <div className='bg-neutral-black/50 absolute inset-0 flex items-center justify-center p-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100'>
                                            <p className='text-canvas text-body-sm line-clamp-3 text-center font-medium'>
                                                {post.caption || 'No Caption'}
                                            </p>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Custom action buttons */}
                        <div className='mt-2 flex w-full gap-2'>
                            <StanButton
                                name='post-picker-clear'
                                variant='ghost'
                                onClick={handleClearAll}
                                disabled={tempSelectedIds.length === 0}
                                className='w-1/4'
                            >
                                Clear
                            </StanButton>
                            <StanButton name='post-picker-confirm' onClick={handleConfirm} className='w-3/4'>
                                {getButtonText()}
                            </StanButton>
                        </div>
                    </div>
                )}
            </div>
        </StanModal>
    );
};
